import { useEffect, useState, type ReactNode } from 'react'
import { fetchJourneyImageUrl, parseJourneyImageRef } from '@/lib/journeyImages'
import { parseMarkdownLinks } from '@/lib/markdownLinks'

/**
 * Minimal markdown renderer for AI chat output — the subset Sparky actually
 * produces: headings, bold/italic, bullet/numbered lists, paragraphs and
 * inline links, plus the journey images users add themselves (spec-38 WP5).
 * Deliberately no markdown dependency (house rule, same as the analyses'
 * `RichText`); anything unrecognised falls through as plain text.
 *
 * Styled by the shared `.vonk-prose` (index.css), like every other rendered
 * rich text: lists, paragraph spacing and the link colour and underline come
 * from there. A chat link only adds weight 500 and darkens on hover.
 */
export function MarkdownLite({ children }: { children: string }) {
  return <div className="vonk-prose">{renderBlocks(children)}</div>
}

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'image'; alt: string; src: string }
  | { type: 'p'; lines: string[] }

const HEADING = /^(#{1,6})\s+(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/
// Only a line that is nothing but an image becomes an image block; an inline
// `![…](…)` stays plain text, as before.
const IMAGE = /^\s*!\[([^\]]*)\]\(([^)\s]+)\)\s*$/

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = []
  for (const line of text.split('\n')) {
    const heading = HEADING.exec(line)
    const bullet = BULLET.exec(line)
    const numbered = NUMBERED.exec(line)
    const image = IMAGE.exec(line)
    const last = blocks[blocks.length - 1]
    if (image) {
      blocks.push({ type: 'image', alt: image[1], src: image[2] })
    } else if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] })
    } else if (bullet) {
      if (last?.type === 'ul') last.items.push(bullet[1])
      else blocks.push({ type: 'ul', items: [bullet[1]] })
    } else if (numbered) {
      if (last?.type === 'ol') last.items.push(numbered[1])
      else blocks.push({ type: 'ol', items: [numbered[1]] })
    } else if (line.trim() === '') {
      // Blank line closes the current block.
      if (last?.type === 'p' && last.lines.length === 0) continue
      blocks.push({ type: 'p', lines: [] })
    } else if (last?.type === 'p') {
      last.lines.push(line)
    } else {
      blocks.push({ type: 'p', lines: [line] })
    }
  }
  return blocks.filter((b) => b.type !== 'p' || b.lines.length > 0)
}

function renderBlocks(text: string): ReactNode[] {
  return parseBlocks(text).map((block, i) => {
    switch (block.type) {
      case 'heading':
        return (
          <p
            key={i}
            className={
              block.level <= 2
                ? 'font-heading text-base font-semibold'
                : 'font-heading text-sm font-semibold'
            }
          >
            {renderInline(block.text)}
          </p>
        )
      case 'ul':
        return (
          <ul key={i}>
            {block.items.map((item, j) => (
              <li key={j}>{renderInline(item)}</li>
            ))}
          </ul>
        )
      case 'ol':
        return (
          <ol key={i}>
            {block.items.map((item, j) => (
              <li key={j}>{renderInline(item)}</li>
            ))}
          </ol>
        )
      case 'image':
        return <JourneyImage key={i} alt={block.alt} src={block.src} />
      case 'p':
        return (
          <p key={i} className="whitespace-pre-wrap">
            {renderInline(block.lines.join('\n'))}
          </p>
        )
    }
  })
}

/**
 * An image the user added to a journey item. Only our own
 * `journey-image:{dealId}/{name}` refs are rendered — they are exchanged for a
 * short-lived signed URL on display, since the bucket is private. Anything else
 * (an external URL, a broken ref) falls back to its alt text, so no third party
 * can be pinged by putting an `![](https://…)` in the content.
 */
function JourneyImage({ alt, src }: { alt: string; src: string }) {
  const ref = parseJourneyImageRef(src)
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (ref === null) return
    let cancelled = false
    fetchJourneyImageUrl(ref.dealId, ref.name)
      .then((signed) => {
        if (!cancelled) setUrl(signed)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
    // The ref object is derived from `src`, so that is the real dependency.
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  if (ref === null || failed) {
    return <p className="text-sm text-muted-foreground">{alt || 'Afbeelding'}</p>
  }
  // .vonk-prose spaces paragraphs and lists with margins (the container used to
  // use a flex gap); an image has none, so it brings a paragraph's bottom margin
  // itself and drops it as the last block, as .vonk-prose does for the others.
  if (url === null) {
    return (
      <div
        className="mb-[.6em] h-24 w-full animate-pulse rounded-lg bg-muted last:mb-0"
        aria-hidden
      />
    )
  }
  return (
    <img
      src={url}
      alt={alt}
      className="mb-[.6em] max-h-96 w-auto max-w-full rounded-lg border border-border/60 last:mb-0"
    />
  )
}

/**
 * Inline pass: `**bold**`, `*italic*` and `[label](url)` links. A `**` that
 * found no partner (AI text often opens a bullet with one) is dropped instead
 * of shown (spec-65 U-039).
 */
function renderInline(text: string): ReactNode[] {
  return text.split(/\*\*([^*]+)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {renderItalic(part)}
      </strong>
    ) : (
      <span key={i}>{renderItalic(part.replace(/\*\*/g, ''))}</span>
    ),
  )
}

/** A lone `*` glued to the start of a word, left after the italic pairs (U-039). */
const LONE_ASTERISK = /(^|\s)\*(?=[^\s*])/g

function renderItalic(text: string): ReactNode[] {
  return text
    .split(/(?<![*\w])\*([^*]+)\*(?![*\w])/g)
    .map((part, i) =>
      i % 2 === 1 ? (
        <em key={i}>{renderLinks(part)}</em>
      ) : (
        renderLinks(part.replace(LONE_ASTERISK, '$1'))
      ),
    )
}

function renderLinks(text: string): ReactNode {
  const segments = parseMarkdownLinks(text)
  if (segments.length === 1 && segments[0].type === 'text') return text
  return segments.map((segment, i) =>
    segment.type === 'link' ? (
      <a
        key={i}
        href={segment.href}
        target="_blank"
        rel="noreferrer noopener"
        className="font-medium hover:text-primary-hover"
      >
        {segment.label}
      </a>
    ) : (
      <span key={i}>{segment.value}</span>
    ),
  )
}
